/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { BCode, BCodeAnalysis } from '@src/app/model/bcode';
import { BCodeSchedule } from '@src/app/model/bcode-schedule';
import { Field } from '@src/app/shared/field/Field';
import { Schedule } from '@src/app/model/schedule';
import { DialogOptions, PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';

import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { required, maxChars, numericRange, percentZeroOrOne } from '@src/app/shared/validators';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { Cycle } from '@src/app/model/cycle';
import { ButtonField } from '@src/app/shared/field/ButtonField';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { UnitPageComponent } from '@src/app/modules/unit/unit-page/unit-page.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { BCodePageNavRoute } from '@src/app/routes/bcode-page-nav';
import { SchedulePageNavRoute } from '@src/app/routes/schedule-page-nav';
import { TxnPageNavRoute } from '@src/app/routes/txn-page-nav';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { VatRateService } from '@src/app/modules/txn/VATRate.service';

@Component({
    selector: 'app-b-code-page',
    templateUrl: './b-code-page.component.html',
    styleUrls: ['./b-code-page.component.scss'],
    imports: [FormPageComponent]
})
export class BCodePageComponent extends AbstractPageComponent {
    dataSvc = inject(BCodeService);
    scheduleSvc = inject(ScheduleService);
    private omcSettingsSvc = inject(OmcSettingsService);
    private vatRatesSvc = inject(VatRateService);
    dialog = inject(MatDialog);
    dialogRef = inject<MatDialogRef<UnitPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    allSchedules: Schedule[];
    vatRegistered = false;

    scheduleGrid: GridField = new GridField({
        field: { label: 'Schedule Allocation', value: 'schedule', formColumn: 4, visible: Field.formOnly },
        rowFactory: () => [
            FieldMaker.id(),
            FieldMaker.id({ value: 'scheduleId', name: 'scheduleId' }),
            FormNavLinkComponent.makeDetail('Schedule', 'schedule.name', 'scheduleId', SchedulePageNavRoute),
            FormNumberComponent.make('Percentage', 'percent',
                { format: 'percent', formatParms: '2.3-3', step: .01 }, { validators: [numericRange(0, 100)] }
            ),
            FieldMaker.deleteGridRow(),
        ],
        objFactory: () => {
            const dialogRef = this.dialog.open(PickDialogComponent, {
                data: {
                    config: Schedule.pickDialogFormConfig(),
                    service: this.scheduleSvc,
                    showSearch: false
                }
            });
            return dialogRef.afterClosed().pipe(first()).pipe<BCodeSchedule>(
                map(itemPicked => itemPicked ? new BCodeSchedule({ scheduleId: itemPicked.id, schedule: itemPicked }) : null)
            );
        }, gridValidator: [percentZeroOrOne]
    });

    bSummaryGrid: GridField = new GridField({
        field: { label: 'Budget History', value: 'budgetSummary', formRow: 2, visible: Field.formOnly, sendServer: false },
        rowFactory: this.bSummaryItemRowFactory.bind(this),
    }).override({ excludeFromDocs: true });

    analysisGrid = new GridField({
        field:
        {
            label: 'Transaction Summary', value: 'txnAnalysis', sendServer: false,
            visible: Field.formOnly, formRow: 2
        },
        rowFactory: this.balanceGridRow.bind(this)
    });

    typeField = FormPicklistComponent.make('Type', 'typeId', 'type', { items: BCode.TYPES, refreshes: [this.prepaidRefresh.bind(this)] }, { disable: true });
    prepaidField = FormCheckboxComponent.make('Prepaid', 'prepaid',
    {
        hint: $localize`Are expenses of this category paid in advance.
        If they are when entering purchase invoice you will be required to enter a date range of the period
        to and from that you are purchasing the service for. Once entered, the invoice will be accrued for you
        so the expense will be spread over the service period and not all shown on the transaction date.`,
        formColumn: 2, refresh: this.prepaidRefresh.bind(this)
    });

    vatField = FormPicklistComponent.make('VAT Code', 'vatCode', null, {}, {formColumn: 3, visible: Field.noShow});

    fieldSet = new FieldSet({
        fields: [
            FieldMaker.id(),
            FieldMaker.rev(),
            this.typeField,
            FieldMaker.nameControl({ validators: [required, maxChars(30)] }),
            FormTextComponent.make('Description', 'description', {
                validators: [maxChars(100)],
                hint: $localize`Description of the type of things that are categorised with this budget code`
            }),
            FormNumberComponent.make('Sort Order', 'sort', {}, {
                validators: [required],
                hint: $localize`Numeric sort order to determine where this item appears in reports`,
                formColumn : 2
            }),
            FormCheckboxComponent.make('System', 'system', {
                hint: $localize`Is this a system code which cannot be deleted`, formColumn: 2, disable: true
            }),
            this.vatField,
            FieldMaker.notes({ formColumn: 3 }),
            FormTextComponent.make('Integration Id', 'integrationId', {
                hint: $localize`Id used on data imports or integration with other systems`,
                formColumn: 4,
            }),
            this.prepaidField,
            this.scheduleGrid,
            this.analysisGrid,
            this.bSummaryGrid
        ], formLayout: LAYOUT_OPTIONS.fourColOver1,
    });

    configReady = new ReplaySubject<null>(1);
    config = new FormConfig({
        navRoute: BCodePageNavRoute,
        docPath: 'omc-books/budgeting/budget-codes',
        title: $localize`Budget Codes`,
        help: $localize`Budget codes help us to analyse past spending and plan our future spending`,
        fieldSet: this.fieldSet,
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.beforeEdit.bind(this),
        beforeList: this.beforeList.bind(this),
    });

    constructor() {
        super();
        const vatRatesSvc = this.vatRatesSvc;


        forkJoin({
            schedules: this.scheduleSvc.get<Schedule>(true),
            omcSettings: this.omcSettingsSvc.getOne<OmcSettings>('omcSettings', null),
        }).subscribe(result => {
            this.allSchedules = result.schedules;
            result.schedules.forEach(schedule => {
                this.fieldSet.fields.push(FormNumberComponent.make(schedule.name,
                    'schedule' + schedule.id, { format: 'percent', formatParms: '1.3-3', blankZero: true },
                    { name: schedule.name, disable: true, visible: Field.noForm, excludeFromDocs: true }));
            });
            if (vatRatesSvc.vatRegistered(result.omcSettings)) {
                this.vatRegistered = true;
                this.vatField.visible = Field.showAll;
                this.vatRatesSvc.getVatCodes().subscribe ( codes => this.vatField.setPicklistItems(codes));
            } else {
                this.vatField.visible = Field.noShow;
            }
            this.configReady.next(null);
        })
    }

    beforeList(bcodes: BCode[]) {
        console.log(bcodes);
        bcodes.forEach( bc => {
            bc.schedule.forEach ( bcs => {
                bc['schedule' + bcs.scheduleId] = bcs.percent;
            });
        })
        return bcodes;
    }

    beforeEdit(o: BCode): BCode {
        if (o.system) {
            this.typeField.control.disable({ emitEvent: false });
        } else {
            this.typeField.control.enable({ emitEvent: false });
        }
        o.budgetSummary = this.summariseBudgets(o);
        this.prepaidRefresh(o.type);
        let runningBalance = 0;
        let txnCycleId: uuid = 'starting';
        o.txnAnalysis.slice().reverse().forEach(bai => {
            if (txnCycleId !== bai.txnCycleId) {
                txnCycleId = bai.txnCycleId;
                runningBalance = 0;
            }
            bai.openBalance = runningBalance;
            bai.debits = +bai.debits;
            bai.credits = +bai.credits;
            runningBalance = runningBalance + bai.debits - bai.credits;
            bai.balance = runningBalance;
        })
        console.log(o);
        return o;
    }

    balanceGridRow(o: BCodeAnalysis) {
        const openingBal = FormNumberComponent.makeCurrency("Opening Balance", 'openBalance', { readonly: true });
        if (o && o.openBalance < 0.00) {
            openingBal.cellOpts.style = 'color:red; white-space:nowrap; text-align: right';
        }

        const closingBal = FormNumberComponent.makeCurrency("Balance", 'balance', { readonly: true });
        if (o && o.balance < 0) {
            closingBal.cellOpts.style = 'color:red; white-space:nowrap; text-align: right;';
        }

        return [
            FormTextComponent.make('Period', 'periodName', { readonly: true }),
            FormDateComponent.make('Start Date', 'startDate', { readonly: true }),
            openingBal,
            FormNumberComponent.makeCurrency("Debit", 'debits', { readonly: true }),
            FormNumberComponent.makeCurrency("Credit", 'credits', { readonly: true }),
            FormDateComponent.make('End Date', 'endDate', { readonly: true }),
            closingBal,
            FormNavLinkComponent.make('Items',
                TxnPageNavRoute, (o: BCodeAnalysis) => ({
                    txnPeriodId: o.txnPeriodId, _sort: 'txnDate', _forceTeamId: o.teamId, bCodeId: o.bCodeId
                }),
                { value: 'txns' }
            ),
            FieldMaker.spacer()
        ]
    }

    summariseBudgets(o: BCode) {
        const c = [];
        for (const bschedule of o.budgets) {
            let bsummary = c.find(bs => bs.cycleName === bschedule.cycle.name);
            if (bsummary === undefined) {
                bsummary = this.createSummaryLine(bschedule.cycle);
                c.push(bsummary);
            }
            bsummary['schedule' + bschedule.scheduleId] += bschedule.plannedSpend;
        }
        c.forEach((summary) => {
            const prev = c.find(s => s.cycleId === summary.previousId);
            if (prev) {
                for (const s of this.allSchedules) {
                    summary['previous' + s.id] = prev['schedule' + s.id];
                }
            }
        })
        return c;
    }

    createSummaryLine(cycle: Cycle) {
        const newItem = { cycleName: cycle.name, cycleId: cycle.id, previousId: cycle.previousId }
        for (const s of this.allSchedules) {
            newItem['schedule' + s.id] = 0;
            newItem['previous' + s.id] = 0;
        }
        return newItem;
    }

    calcArrow(c, f: ButtonField, s: Schedule) {
        if (c['schedule' + s.id] > c['previous' + s.id]) {
            f.btnOpts.iconStyle = 'color:red; padding-left: 15px'
            return 'arrow_upward';
        } else if (c['schedule' + s.id] < c['previous' + s.id]) {
            f.btnOpts.iconStyle = 'color:green; padding-left: 15px; padding:right: '
            return 'arrow_downward';
        }
        return '';
    }

    bSummaryItemRowFactory() {
        const fields = [
            FormTextComponent.make('Cycle', 'cycleName', { readonly: true }),
        ];
        for (const s of this.allSchedules) {
            fields.push(FormButtonComponent.make('', '', {
                name: 'arrow' + s.id, type: 'icon', sendServer: false, label: '',
                cellOpts: { heading: '', width: '1%', style: 'padding: 0px 0px 0px 0px; ' },
                calculateValue: (o: any, f: ButtonField) => this.calcArrow(o, f, s),
            }));
            fields.push(FormNumberComponent.makeCurrency(s.name, 'schedule' + s.id, { readonly: true, }).blankZero());
            fields.push(FormNumberComponent.makePercent('%', 'previous' + s.id,
                {
                    readonly: true, cellOpts: { style: 'align: right; font: lighter' }, calculateValue: (c) => {
                        if (c['previous' + s.id]) {
                            return (c['schedule' + s.id] - c['previous' + s.id]) / c['previous' + s.id];
                        } else if (c['schedule' + s.id] > 0) {
                            return 1;
                        } else {
                            return 0;
                        }
                    }
                }).blankZero());
        }
        fields.push(FieldMaker.spacer());
        return fields;
    }

    prepaidRefresh(o: AbstractObject) {
        if (o.id === BCode.TYPE.EXPENSE.id) {
            this.prepaidField.setReadonly(false);
            this.prepaidField.enableControl();
        } else {
            this.prepaidField.setReadonly(true);
            this.prepaidField.disableControl();
        }
        //this.prepaidField.e = (o.id === BCode.TYPE.EXPENSE.id) ? Field.formOnly : Field.noShow;
    }

    newFactory(): Observable<BCode> {
        this.typeField.control.enable({ emitEvent: false });
        const bc = new BCode();
        this.prepaidRefresh(BCode.TYPE.EXPENSE);
        if (this.vatRegistered) {
            bc.vatCode = 'S';
        } else {
            bc.vatCode = 'x';
        }
        if (this.dialogOptions) {
            bc.name = this.dialogOptions['name'];
            bc.sort = this.dialogOptions['sort'];
            bc.typeId = this.dialogOptions['typeId'];
            bc.integrationId = this.dialogOptions['integrationId'];
        }
        return of(bc);
    }
}
